import React, { useState, useEffect, useRef } from 'react';
import { apiService } from '../services/apiService';
import { X, FileText, ChevronRight, ChevronLeft, CheckSquare, Square, Building2, Truck, Calendar, Pencil, Search, Plus, Trash2, Activity } from 'lucide-react';
import toast from 'react-hot-toast';

// ── Helpers ──────────────────────────────────────────────────────────────────

/** Parse "YYYY-MM-DD" como data local, evitando UTC offset */
function parseLocalDate(str) {
  if (!str) return new Date();
  if (str instanceof Date) return str;
  const [y, m, d] = String(str).split('T')[0].split('-').map(Number);
  return new Date(y, m - 1, d);
}

function toInputDate(date) {
  if (!date) return '';
  const d = date instanceof Date ? date : parseLocalDate(date);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
}

function countWorkingDays(start, end) {
  let count = 0;
  const cur = new Date(start);
  const endD = end instanceof Date ? end : parseLocalDate(end);
  while (cur <= endD) {
    const dow = cur.getDay();
    if (dow !== 0 && dow !== 6) count++;
    cur.setDate(cur.getDate() + 1);
  }
  return count;
}

function fmt(date) {
  if (!date) return '—';
  const d = date instanceof Date ? date : parseLocalDate(date);
  return d.toLocaleDateString('pt-BR');
}

function fmtMoney(val) {
  return Number(val || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function generateSuggestedPeriods(contract, existingInvoices) {
  const endDate = parseLocalDate(contract.end_date);

  // Âncora do ciclo: dia do period_start da 1ª fatura já gerada (backend usou signed_at).
  // Fallback: start_date do contrato.
  let anchorDay;
  const sortedInvoices = [...existingInvoices]
    .filter(i => i.period_start)
    .sort((a, b) => parseLocalDate(a.period_start) - parseLocalDate(b.period_start));
  if (sortedInvoices.length > 0) {
    anchorDay = parseLocalDate(sortedInvoices[0].period_start).getDate();
  } else {
    anchorDay = parseLocalDate(contract.start_date).getDate();
  }
  const startDay = anchorDay;

  const diaVenc = parseInt(contract.dia_vencimento_boleto) || 15;
  const monthlyRate = parseFloat(contract.monthly_rate) || 0;
  const insuranceValue = contract.insurance_required ? parseFloat(contract.insurance_value) || 0 : 0;
  const totalMonthly = monthlyRate + insuranceValue;

  const periods = [];
  // Sempre gerar todos os ciclos desde o ciclo 1, marcando os já faturados como already_generated.
  // Isso garante que se uma fatura for deletada (ex: ciclo 1), ela reaparece como disponível.
  const contractStart = parseLocalDate(contract.start_date);
  let cycleMonth = contractStart.getMonth() + 1;
  let cycleYear = contractStart.getFullYear();
  if (cycleMonth > 12) { cycleMonth = cycleMonth - 12; cycleYear++; }

  while (true) {
    const periodStart = new Date(cycleYear, cycleMonth - 1, startDay);
    if (periodStart >= endDate) break;

    const periodEnd = new Date(cycleYear, cycleMonth, startDay - 1);
    const dueDate = new Date(cycleYear, cycleMonth - 1, diaVenc);

    // Verificar se já existe fatura para este ciclo
    // Usa reference_month/reference_year (ciclo de cobrança) com fallback para period_start
    const alreadyGenerated = existingInvoices.some(inv => {
      if (!inv.period_start) return false;
      if ((inv.status || '').toLowerCase() === 'cancelled') return false;
      const invMonth = inv.reference_month != null ? inv.reference_month : parseLocalDate(inv.period_start).getMonth() + 1;
      const invYear = inv.reference_year != null ? inv.reference_year : parseLocalDate(inv.period_start).getFullYear();
      return invMonth === cycleMonth && invYear === cycleYear;
    });

    const workingDays = countWorkingDays(periodStart, periodEnd);
    const calendarDays = Math.round((periodEnd - periodStart) / (1000 * 60 * 60 * 24)) + 1;
    const dailyRate = monthlyRate / 30;

    periods.push({
      cycle: periods.length + 1,
      period_start: periodStart,
      period_end: periodEnd,
      due_date: dueDate,
      calendar_days: calendarDays,
      working_days: workingDays,
      daily_rate: dailyRate,
      total: monthlyRate + insuranceValue,
      monthly_rate: monthlyRate,
      insurance_value: insuranceValue,
      days_worked: 30,
      already_generated: alreadyGenerated,
      year: cycleYear,
      month: cycleMonth,
    });

    cycleMonth++;
    if (cycleMonth > 12) { cycleMonth = 1; cycleYear++; }
  }

  return periods;
}

// ── Editable cell ─────────────────────────────────────────────────────────────

function EditableDate({ value, onChange, disabled }) {
  return (
    <input
      type="date"
      value={toInputDate(value)}
      disabled={disabled}
      onClick={e => e.stopPropagation()}
      onChange={e => {
        e.stopPropagation();
        if (e.target.value) onChange(parseLocalDate(e.target.value));
      }}
      className="w-32 border border-gray-200 dark:border-gray-600 rounded px-2 py-1 text-xs bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-1 focus:ring-blue-500 disabled:opacity-50"
    />
  );
}

function EditableNumber({ value, onChange, disabled }) {
  const [editing, setEditing] = React.useState(false);
  const [raw, setRaw] = React.useState('');
  const display = editing ? raw : (value === '' || value == null ? '' : Number(value).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
  return (
    <input
      type="text"
      inputMode="decimal"
      value={display}
      disabled={disabled}
      onClick={e => e.stopPropagation()}
      onFocus={e => {
        setEditing(true);
        setRaw(String(value ?? ''));
        setTimeout(() => e.target.select(), 0);
      }}
      onChange={e => setRaw(e.target.value)}
      onBlur={e => {
        setEditing(false);
        const parsed = parseFloat(e.target.value.replace(',', '.')) || 0;
        onChange(parsed);
      }}
      className="w-24 border border-gray-200 dark:border-gray-600 rounded px-2 py-1 text-xs bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-1 focus:ring-blue-500 text-right disabled:opacity-50"
    />
  );
}

// ── Component ─────────────────────────────────────────────────────────────────

const GenerateInvoicesModal = ({ isOpen, onClose, onSuccess }) => {
  const [step, setStep] = useState(1);

  const [clients, setClients] = useState([]);
  const [loadingClients, setLoadingClients] = useState(false);
  const [selectedClientId, setSelectedClientId] = useState('');
  const [clientSearch, setClientSearch] = useState('');
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  const clientSearchRef = useRef(null);

  const [allContracts, setAllContracts] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [loadingContracts, setLoadingContracts] = useState(false);
  const [selectedContract, setSelectedContract] = useState(null);

  const [periods, setPeriods] = useState([]);
  const [loadingPeriods, setLoadingPeriods] = useState(false);
  const [selectedPeriods, setSelectedPeriods] = useState([]);

  const [generating, setGenerating] = useState(false);
  const [isMedicao, setIsMedicao] = useState(false);
  const [sendChargeEmail, setSendChargeEmail] = useState(false);
  const [emailDaysBefore, setEmailDaysBefore] = useState(5);

  // Recalcular totais ao mudar modo medição
  const toggleMedicao = (value) => {
    setIsMedicao(value);
    setPeriods(prev => prev.map(p => {
      if (p.already_generated) return p;
      if (value) {
        const days = p.days_worked ?? 30;
        const fator = parseFloat((days / 30).toFixed(4));
        return {
          ...p,
          days_worked: days,
          total: parseFloat((fator * p.monthly_rate).toFixed(2)),
        };
      } else {
        // Modo normal: total mensal cheio
        return {
          ...p,
          total: p.monthly_rate + (p.insurance_value || 0),
        };
      }
    }));
  };

  useEffect(() => {
    if (!isOpen) return;
    setStep(1);
    setSelectedClientId('');
    setClientSearch('');
    setShowClientDropdown(false);
    setSelectedContract(null);
    setPeriods([]);
    setSelectedPeriods([]);
    setAllContracts([]);
    setIsMedicao(false);
    fetchClients();
    fetchAllContracts();
  }, [isOpen]);

  const fetchClients = async () => {
    setLoadingClients(true);
    try {
      const res = await apiService.getClients({ status: 'active', limit: 200 });
      const list = res.data || res || [];
      setClients(Array.isArray(list) ? list : []);
    } catch {
      toast.error('Erro ao carregar clientes');
    } finally {
      setLoadingClients(false);
    }
  };

  const fetchAllContracts = async () => {
    try {
      const res = await apiService.getContracts({});
      const list = res.data || res || [];
      setAllContracts(Array.isArray(list) ? list : []);
    } catch { /* silent */ }
  };

  const handleSelectClient = async (clientId) => {
    setSelectedClientId(clientId);
    setSelectedContract(null);
    setPeriods([]);
    setSelectedPeriods([]);
    if (!clientId) return;

    setLoadingContracts(true);
    try {
      let list = allContracts;
      if (list.length === 0) {
        const res = await apiService.getContracts({});
        list = res.data || res || [];
        setAllContracts(Array.isArray(list) ? list : []);
      }
      const filtered = list.filter(
        c => c.client_id === clientId && c.status === 'active'
      );
      setContracts(filtered);
      setStep(2);
    } catch {
      toast.error('Erro ao carregar contratos');
    } finally {
      setLoadingContracts(false);
    }
  };

  const handleSelectContract = async (contract) => {
    setSelectedContract(contract);
    setLoadingPeriods(true);
    try {
      const res = await apiService.getInvoices({ contractId: contract.id, limit: 200 });
      const existingInvoices = res.data || res || [];
      const suggested = generateSuggestedPeriods(contract, Array.isArray(existingInvoices) ? existingInvoices : []);
      setPeriods(suggested);
      setSelectedPeriods(suggested.filter(p => !p.already_generated).map(p => p.cycle));
      setStep(3);
    } catch {
      toast.error('Erro ao carregar faturas existentes');
    } finally {
      setLoadingPeriods(false);
    }
  };

  /** Atualiza um campo de um período e recalcula dependentes */
  const updatePeriod = (cycle, field, value) => {
    setPeriods(prev => prev.map(p => {
      if (p.cycle !== cycle) return p;
      const updated = { ...p, [field]: value };

      // Recalcular dias corridos ao mudar datas
      if (field === 'period_start' || field === 'period_end') {
        const start = field === 'period_start' ? value : p.period_start;
        const end = field === 'period_end' ? value : p.period_end;
        const cd = Math.max(1, Math.round((end - start) / (1000 * 60 * 60 * 24)) + 1);
        updated.calendar_days = cd;
        updated.working_days = countWorkingDays(start, end);
        // No modo medição, sincronizar DIAS com dias corridos do período editado
        updated.days_worked = cd;
        const fator = parseFloat((cd / 30).toFixed(4));
        updated.total = parseFloat((fator * p.monthly_rate).toFixed(2));
      }

      // Recalcular total ao mudar valor/dia ou dias
      if (field === 'daily_rate') {
        updated.total = value * 30;
      }
      if (field === 'total') {
        updated.daily_rate = value / 30;
      }

      // Recalcular total ao mudar dias trabalhados (medição)
      // Fórmula: round(dias/30, 2) × mensal
      if (field === 'days_worked') {
        const fator = parseFloat((value / 30).toFixed(4));
        updated.total = parseFloat((fator * p.monthly_rate).toFixed(2));
      }

      // Recalcular total ao mudar valor mensal (medição)
      if (field === 'monthly_rate') {
        const days = p.days_worked ?? p.working_days;
        const fator = parseFloat((days / 30).toFixed(4));
        updated.total = parseFloat((fator * value).toFixed(2));
      }

      return updated;
    }));
  };

  const togglePeriod = (cycle) => {
    setSelectedPeriods(prev =>
      prev.includes(cycle) ? prev.filter(c => c !== cycle) : [...prev, cycle]
    );
  };

  const toggleAll = () => {
    const selectable = periods.filter(p => !p.already_generated).map(p => p.cycle);
    setSelectedPeriods(prev =>
      prev.length === selectable.length ? [] : selectable
    );
  };

  const addExtraPeriod = () => {
    const last = periods[periods.length - 1];
    if (!last) return;

    // Próximo ciclo: avança 1 mês a partir do último período
    let nextMonth = last.month + 1;
    let nextYear = last.year;
    if (nextMonth > 12) { nextMonth = 1; nextYear++; }

    const startDay = last.period_start instanceof Date
      ? last.period_start.getDate()
      : parseLocalDate(last.period_start).getDate();

    const periodStart = new Date(nextYear, nextMonth - 1, startDay);
    const periodEnd   = new Date(nextYear, nextMonth, startDay - 1);
    const dueDate     = new Date(nextYear, nextMonth - 1, last.due_date instanceof Date
      ? last.due_date.getDate()
      : parseLocalDate(last.due_date).getDate());

    const calendarDays = Math.round((periodEnd - periodStart) / (1000 * 60 * 60 * 24)) + 1;
    const workingDays  = countWorkingDays(periodStart, periodEnd);
    const nextCycle    = last.cycle + 1;

    const extra = {
      cycle: nextCycle,
      period_start: periodStart,
      period_end: periodEnd,
      due_date: dueDate,
      calendar_days: calendarDays,
      working_days: workingDays,
      daily_rate: last.daily_rate,
      total: last.monthly_rate + (last.insurance_value || 0),
      monthly_rate: last.monthly_rate,
      insurance_value: last.insurance_value,
      days_worked: 30,
      already_generated: false,
      year: nextYear,
      month: nextMonth,
      extra: true,
    };

    setPeriods(prev => [...prev, extra]);
    setSelectedPeriods(prev => [...prev, nextCycle]);
  };

  const removeExtraPeriod = (cycle) => {
    setPeriods(prev => prev.filter(p => p.cycle !== cycle));
    setSelectedPeriods(prev => prev.filter(c => c !== cycle));
  };

  const handleGenerate = async () => {
    const toGenerate = periods.filter(p => selectedPeriods.includes(p.cycle));
    if (toGenerate.length === 0) {
      toast.error('Selecione pelo menos um período');
      return;
    }

    setGenerating(true);
    try {
      const response = await apiService.generateMonthlyInvoices({
        contractIds: [selectedContract.id],
        months: toGenerate.map(p => ({
          year: p.year,
          month: p.month,
          due_date: toInputDate(p.due_date),
          period_start: toInputDate(p.period_start),
          period_end: toInputDate(p.period_end),
          days_worked: isMedicao ? (p.days_worked ?? p.working_days) : null,
          override_total: parseFloat(p.total) || null,
        })),
        sendChargeEmail,
        emailDaysBefore: sendChargeEmail ? emailDaysBefore : undefined,
      });

      const data = response.data || response;
      const successCount = Array.isArray(data.success) ? data.success.length : 0;
      const skippedCount = Array.isArray(data.skipped) ? data.skipped.length : 0;
      const errorCount = Array.isArray(data.errors) ? data.errors.length : 0;

      toast.success(
        `✅ Criadas: ${successCount}  ⏭ Ignoradas: ${skippedCount}  ❌ Erros: ${errorCount}`,
        { duration: 5000 }
      );

      onSuccess();
      onClose();
    } catch (err) {
      toast.error('Erro ao gerar: ' + (err.response?.data?.message || err.message));
    } finally {
      setGenerating(false);
    }
  };

  if (!isOpen) return null;

  const selectedClient = clients.find(c => c.id === selectedClientId);
  const selectableCount = periods.filter(p => !p.already_generated).length;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-5xl max-h-[92vh] overflow-hidden flex flex-col">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-xl">
              <FileText className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">Gerar Faturas Mensais</h2>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {step === 1 && 'Passo 1 de 3 — Selecione o cliente'}
                {step === 2 && `Passo 2 de 3 — Contratos de ${selectedClient?.company_name || selectedClient?.name || ''}`}
                {step === 3 && `Passo 3 de 3 — Períodos · ${selectedContract?.license_plate || ''} · ${selectedContract?.client_name || ''}`}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Breadcrumb */}
        <div className="flex items-center gap-2 px-6 py-3 bg-gray-50 dark:bg-gray-900/40 border-b border-gray-200 dark:border-gray-700 text-sm">
          {[
            { n: 1, label: 'Cliente', icon: Building2 },
            { n: 2, label: 'Contrato', icon: Truck },
            { n: 3, label: 'Períodos', icon: Calendar },
          ].map(({ n, label, icon: Icon }, i) => (
            <React.Fragment key={n}>
              <button
                disabled={n > step}
                onClick={() => n < step && setStep(n)}
                className={`flex items-center gap-1.5 px-3 py-1 rounded-full font-medium transition ${
                  step === n ? 'bg-blue-600 text-white'
                  : n < step ? 'text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 cursor-pointer'
                  : 'text-gray-400 dark:text-gray-600 cursor-not-allowed'
                }`}
              >
                <Icon className="w-3.5 h-3.5" />
                {label}
              </button>
              {i < 2 && <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />}
            </React.Fragment>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">

          {/* Step 1 */}
          {step === 1 && (
            <div className="flex flex-col gap-3">
              {/* Combobox: input + lista integrada */}
              <div className="border border-gray-300 dark:border-gray-600 rounded-xl overflow-hidden shadow-sm focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500">
                {/* Search bar */}
                <div className="flex items-center gap-2 px-4 py-3 bg-white dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
                  <Search className="w-4 h-4 text-gray-400 flex-shrink-0" />
                  <input
                    ref={clientSearchRef}
                    type="text"
                    placeholder="Buscar cliente por nome..."
                    value={clientSearch}
                    autoFocus
                    onChange={e => {
                      setClientSearch(e.target.value);
                      if (!e.target.value) setSelectedClientId('');
                    }}
                    className="flex-1 bg-transparent text-gray-900 dark:text-white text-base outline-none placeholder-gray-400"
                  />
                  {clientSearch && (
                    <button
                      onClick={() => { setSelectedClientId(''); setClientSearch(''); clientSearchRef.current?.focus(); }}
                      className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                  {loadingClients && <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 flex-shrink-0" />}
                </div>

                {/* List */}
                {!loadingClients && (
                  <div className="max-h-64 overflow-y-auto bg-white dark:bg-gray-700 divide-y divide-gray-100 dark:divide-gray-600">
                    {(() => {
                      const q = clientSearch.toLowerCase().trim();
                      const filtered = q
                        ? clients.filter(c => (c.company_name || c.name || '').toLowerCase().includes(q))
                        : clients;

                      if (filtered.length === 0) {
                        return (
                          <div className="px-4 py-10 text-center text-gray-400 text-sm">
                            Nenhum cliente encontrado para "{clientSearch}"
                          </div>
                        );
                      }

                      return filtered.map(c => {
                        const label = c.company_name || c.name || c.id;
                        const isSelected = c.id === selectedClientId;
                        return (
                          <button
                            key={c.id}
                            onClick={() => {
                              setClientSearch(label);
                              handleSelectClient(c.id);
                            }}
                            className={`w-full text-left px-4 py-3 text-sm flex items-center gap-3 transition ${
                              isSelected
                                ? 'bg-blue-600 text-white'
                                : 'hover:bg-blue-50 dark:hover:bg-blue-900/20 text-gray-800 dark:text-gray-200'
                            }`}
                          >
                            <Building2 className={`w-4 h-4 flex-shrink-0 ${isSelected ? 'text-white' : 'text-gray-400'}`} />
                            {q ? (
                              <span dangerouslySetInnerHTML={{
                                __html: label.replace(
                                  new RegExp(`(${q.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi'),
                                  '<mark class="bg-yellow-200 dark:bg-yellow-600 rounded-sm px-0.5 text-gray-900">$1</mark>'
                                )
                              }} />
                            ) : label}
                          </button>
                        );
                      });
                    })()}
                  </div>
                )}
              </div>

              {loadingContracts && (
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600" />
                  Carregando contratos...
                </div>
              )}
            </div>
          )}

          {/* Step 2 */}
          {step === 2 && (
            <div>
              {contracts.length === 0 ? (
                <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                  Nenhum contrato ativo com fatura inicial gerada.
                </div>
              ) : (
                <div className="space-y-3">
                  {contracts.map(contract => (
                    <button
                      key={contract.id}
                      onClick={() => handleSelectContract(contract)}
                      className="w-full text-left p-4 border-2 border-gray-200 dark:border-gray-700 hover:border-blue-500 rounded-xl transition group"
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-xl bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center flex-shrink-0">
                          <Truck className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-semibold text-gray-900 dark:text-white">
                              {contract.license_plate || contract.vehicle_license_plate}
                            </span>
                            <span className="text-sm text-gray-600 dark:text-gray-400">
                              {contract.vehicle_brand} {contract.vehicle_model}
                            </span>
                          </div>
                          <div className="flex gap-4 mt-1 text-xs text-gray-500 dark:text-gray-400 flex-wrap">
                            <span>Início: {fmt(contract.start_date)}</span>
                            <span>Término: {fmt(contract.end_date)}</span>
                            <span className="text-green-600 dark:text-green-400 font-medium">
                              {fmtMoney(contract.monthly_rate)}/mês
                            </span>
                            {contract.insurance_required && parseFloat(contract.insurance_value) > 0 && (
                              <span className="text-orange-500">+ Seguro {fmtMoney(contract.insurance_value)}</span>
                            )}
                          </div>
                        </div>
                        <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-blue-500 transition flex-shrink-0" />
                      </div>
                    </button>
                  ))}
                </div>
              )}
              {loadingPeriods && (
                <div className="flex items-center gap-2 mt-4 text-sm text-gray-500">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600" />
                  Calculando períodos...
                </div>
              )}
            </div>
          )}

          {/* Step 3 */}
          {step === 3 && (
            <div>
              {/* Toggle: Fatura por Medição */}
              <label className="flex items-center gap-2 mb-3 cursor-pointer select-none w-fit">
                <div
                  onClick={() => toggleMedicao(!isMedicao)}
                  className={`relative w-10 h-5 rounded-full transition-colors ${isMedicao ? 'bg-blue-600' : 'bg-gray-300 dark:bg-gray-600'}`}
                >
                  <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${isMedicao ? 'translate-x-5' : ''}`} />
                </div>
                <Activity className={`w-4 h-4 ${isMedicao ? 'text-blue-600' : 'text-gray-400'}`} />
                <span className={`text-sm font-medium ${isMedicao ? 'text-blue-700 dark:text-blue-400' : 'text-gray-600 dark:text-gray-400'}`}>
                  Fatura por Medição <span className="font-normal text-xs">(cobrança por dias trabalhados)</span>
                </span>
              </label>

              <div className="flex items-center justify-between mb-3">
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  <span className="font-semibold text-blue-600 dark:text-blue-400">{selectedPeriods.length}</span>
                  {' '}selecionado(s) de{' '}
                  <span className="font-semibold">{selectableCount}</span> disponíveis
                  <span className="ml-2 text-xs text-gray-400 inline-flex items-center gap-1">
                    <Pencil className="w-3 h-3" /> campos editáveis
                  </span>
                </div>
                <button onClick={toggleAll} className="text-sm text-blue-600 dark:text-blue-400 hover:underline font-medium">
                  {selectedPeriods.length === selectableCount ? 'Desmarcar Todos' : 'Selecionar Todos'}
                </button>
              </div>

              {periods.length === 0 ? (
                <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                  Todos os períodos já foram faturados ou o contrato está vencido.
                </div>
              ) : (
                <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-700">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-gray-50 dark:bg-gray-900/50 text-gray-500 dark:text-gray-400 text-xs uppercase tracking-wide">
                        <th className="px-2 py-3 text-left w-8"></th>
                        <th className="px-2 py-3 text-center w-8">#</th>
                        <th className="px-2 py-3 text-left">Início</th>
                        <th className="px-2 py-3 text-left">Fim</th>
                        <th className="px-2 py-3 text-left">Vencimento</th>
                        {!isMedicao && <th className="px-2 py-3 text-center">Corridos</th>}
                        {!isMedicao && <th className="px-2 py-3 text-center">Úteis</th>}
                        {isMedicao && <th className="px-2 py-3 text-center text-blue-600 dark:text-blue-400">Dias</th>}
                        <th className="px-3 py-3 text-right">{isMedicao ? 'Valor Mensal' : 'Valor/Dia'}</th>
                        <th className="px-3 py-3 text-right">Total</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                      {periods.map(period => {
                        const isSelected = selectedPeriods.includes(period.cycle);
                        const isDisabled = period.already_generated;
                        return (
                          <tr
                            key={period.cycle}
                            onClick={() => !isDisabled && !period.extra && togglePeriod(period.cycle)}
                            className={`transition align-middle ${
                              isDisabled
                                ? 'bg-gray-50 dark:bg-gray-900/20 opacity-50 cursor-not-allowed'
                                : period.extra
                                ? 'bg-amber-50 dark:bg-amber-900/10 cursor-default'
                                : isSelected
                                ? 'bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/30 cursor-pointer'
                                : 'hover:bg-gray-50 dark:hover:bg-gray-700/30 cursor-pointer'
                            }`}
                          >
                            <td className="px-2 py-2">
                              {isDisabled ? (
                                <span className="text-xs text-green-600">✓</span>
                              ) : period.extra ? (
                                <button
                                  onClick={e => { e.stopPropagation(); removeExtraPeriod(period.cycle); }}
                                  className="text-red-400 hover:text-red-600 transition"
                                  title="Remover"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              ) : isSelected ? (
                                <CheckSquare className="w-4 h-4 text-blue-600" />
                              ) : (
                                <Square className="w-4 h-4 text-gray-400" />
                              )}
                            </td>
                            <td className="px-2 py-2 text-center text-xs">
                              <span className="font-mono text-gray-400">{period.cycle}°</span>
                              {period.extra && (
                                <span className="ml-1 px-1 py-0.5 bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-400 rounded text-xs font-medium">extra</span>
                              )}
                            </td>
                            {/* Início do período */}
                            <td className="px-2 py-2">
                              <EditableDate
                                value={period.period_start}
                                disabled={isDisabled}
                                onChange={v => updatePeriod(period.cycle, 'period_start', v)}
                              />
                              {isDisabled && (
                                <div className="text-xs text-green-600 mt-0.5">Já faturado</div>
                              )}
                            </td>
                            {/* Fim do período */}
                            <td className="px-2 py-2">
                              <EditableDate
                                value={period.period_end}
                                disabled={isDisabled}
                                onChange={v => updatePeriod(period.cycle, 'period_end', v)}
                              />
                            </td>
                            {/* Vencimento */}
                            <td className="px-2 py-2">
                              <EditableDate
                                value={period.due_date}
                                disabled={isDisabled}
                                onChange={v => updatePeriod(period.cycle, 'due_date', v)}
                              />
                            </td>
                            {/* Dias corridos */}
                            {!isMedicao && (
                              <td className="px-2 py-2 text-center">
                                <EditableNumber
                                  value={period.calendar_days}
                                  disabled={isDisabled}
                                  onChange={v => updatePeriod(period.cycle, 'calendar_days', v)}
                                />
                              </td>
                            )}
                            {/* Dias úteis */}
                            {!isMedicao && (
                              <td className="px-2 py-2 text-center">
                                <EditableNumber
                                  value={period.working_days}
                                  disabled={isDisabled}
                                  onChange={v => updatePeriod(period.cycle, 'working_days', v)}
                                />
                              </td>
                            )}
                            {/* Dias trabalhados (medição) */}
                            {isMedicao && (
                              <td className="px-2 py-2 text-center">
                                <EditableNumber
                                  value={period.days_worked ?? period.working_days}
                                  disabled={isDisabled}
                                  onChange={v => updatePeriod(period.cycle, 'days_worked', v)}
                                />
                                {!isDisabled && (
                                  <div className="text-xs text-blue-500 leading-none" style={{ marginTop: 2 }}>
                                    {((period.days_worked ?? period.working_days) / 30).toFixed(4)}×
                                  </div>
                                )}
                              </td>
                            )}
                            {/* Valor/dia ou Valor Mensal (medição) */}
                            <td className="px-3 py-2 text-right">
                              <EditableNumber
                                value={isMedicao ? Number(period.monthly_rate).toFixed(2) : Number(period.daily_rate).toFixed(2)}
                                disabled={isDisabled}
                                prefix="R$"
                                onChange={v => updatePeriod(period.cycle, isMedicao ? 'monthly_rate' : 'daily_rate', v)}
                              />
                            </td>
                            {/* Total */}
                            <td className="px-3 py-2 text-right">
                              <EditableNumber
                                value={Number(period.total).toFixed(2)}
                                disabled={isDisabled}
                                prefix="R$"
                                onChange={v => updatePeriod(period.cycle, 'total', v)}
                              />
                              {period.insurance_value > 0 && !isDisabled && (
                                <div className="text-xs text-orange-500 text-right mt-0.5">
                                  incl. {fmtMoney(period.insurance_value)} seg.
                                </div>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                    {selectedPeriods.length > 0 && (
                      <tfoot>
                        <tr className="bg-blue-50 dark:bg-blue-900/20 font-semibold text-blue-700 dark:text-blue-300">
                          <td colSpan={isMedicao ? 7 : 8} className="px-3 py-3 text-right text-sm">
                            Total a faturar ({selectedPeriods.length} fatura{selectedPeriods.length > 1 ? 's' : ''}):
                          </td>
                          <td className="px-3 py-3 text-right font-bold text-base">
                            {fmtMoney(
                              periods
                                .filter(p => selectedPeriods.includes(p.cycle))
                                .reduce((s, p) => s + p.total, 0)
                            )}
                          </td>
                        </tr>
                      </tfoot>
                    )}
                  </table>
                </div>
              )}

              {/* Botão adicionar período extra */}
              {periods.length > 0 && (
                <button
                  onClick={addExtraPeriod}
                  className="mt-3 flex items-center gap-2 px-4 py-2 border-2 border-dashed border-blue-300 dark:border-blue-700 text-blue-600 dark:text-blue-400 rounded-xl hover:border-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition text-sm font-medium w-full justify-center"
                >
                  <Plus className="w-4 h-4" />
                  Adicionar período extra
                </button>
              )}

              {/* Envio automático de boleto */}
              {periods.length > 0 && (
                <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                  <label className="flex items-center gap-3 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={sendChargeEmail}
                      onChange={e => setSendChargeEmail(e.target.checked)}
                      className="w-4 h-4 rounded border-gray-300 text-blue-600 cursor-pointer"
                    />
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Enviar boleto por email automaticamente
                    </span>
                  </label>
                  {sendChargeEmail && (
                    <div className="mt-3 ml-7">
                      <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                        Dias antes do vencimento para enviar
                      </label>
                      <input
                        type="number"
                        min={1}
                        max={30}
                        value={emailDaysBefore}
                        onChange={e => setEmailDaysBefore(Math.max(1, parseInt(e.target.value) || 1))}
                        className="w-24 px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      />
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        O boleto será enviado {emailDaysBefore} dia{emailDaysBefore !== 1 ? 's' : ''} antes do vencimento. No dia do vencimento, um lembrete também será enviado.
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
          <button
            onClick={() => step > 1 ? setStep(step - 1) : onClose()}
            disabled={generating}
            className="flex items-center gap-2 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition disabled:opacity-50 font-medium"
          >
            <ChevronLeft className="w-4 h-4" />
            {step > 1 ? 'Voltar' : 'Cancelar'}
          </button>

          {step === 3 && (
            <button
              onClick={handleGenerate}
              disabled={generating || selectedPeriods.length === 0}
              className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed font-semibold"
            >
              {generating ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                  Gerando...
                </>
              ) : (
                <>
                  <FileText className="w-4 h-4" />
                  Gerar {selectedPeriods.length} Fatura{selectedPeriods.length !== 1 ? 's' : ''}
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default GenerateInvoicesModal;
